import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AccountMarkerInfo, AccountMarkerOverlayProps } from './types';
import { AccountType } from '../../../../__generated__/pge-types';
import { AccountWithOutageInfo, OutageLngLat } from '../../types';
import { Marker } from 'react-map-gl';
import mainColors from '../../../../themes/main-colors';
import ServiceLocationIcon from '../icons/ServiceLocationIcon';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';
import CommercialIcon from '../icons/CommercialIcon';
import { useOutageMapContext } from '../../providers/OutageMapProvider';
import { getAccountsByType, getReportedOutageAccountInfo } from './utils';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';
import PlannedOutageIcon from '../icons/PlannedOutageIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    icon: {
      width: '48px',
      height: '48px',
      transition: '0.2s',
      '&:hover': {
        width: '56px !important',
        height: '56px !important',
      },
    },
    iconSelected: {
      width: '56px',
      height: '56px',
    },
  }),
);

export const AccountMarkerOverlay: React.FC<AccountMarkerOverlayProps> = ({
  accountDetails,
  selectedAccount,
}) => {
  const classes = useStyles();
  const [accountMarkersList, setAccountMarkersList] = useState<
    AccountMarkerInfo[]
  >([]);
  const {
    setSelectedAccountForOutage,
    setOutageMarkerView,
  } = useOutageLeftPanelState();
  const { fitBounds } = useOutageMapContext();
  const { setPage } = useOutageResponsivePageTracker();

  const getOutageAccountDetails = useCallback(() => {
    const accountDetailsList: AccountWithOutageInfo[] = [];

    const commercialAccount = getAccountsByType(
      accountDetails,
      AccountType.Com,
    );
    const residentialAccounts = getAccountsByType(
      accountDetails,
      AccountType.Res,
    );
    const primaryResAccount = residentialAccounts.filter(
      account => account.isPrimaryAccount,
    );
    const primaryCommAccount = commercialAccount.filter(
      account => account.isPrimaryAccount,
    );

    const allPrimaryAccounts = [...primaryResAccount, ...primaryCommAccount];

    if (allPrimaryAccounts?.length) {
      allPrimaryAccounts.forEach(account => {
        if (account) {
          accountDetailsList.push(account);
        }
      });
    }

    //If there were no primary Commercial Accounts but we have commercial accounts then display the first commercial account received from gql query.
    if (commercialAccount?.length && !primaryCommAccount?.length) {
      if (commercialAccount[0]) {
        accountDetailsList.push(commercialAccount[0]);
      }
    }

    // display all residentialAccounts
    if (residentialAccounts?.length) {
      residentialAccounts
        .filter(acc => !acc.isPrimaryAccount)
        .forEach(account => {
          if (account) {
            accountDetailsList.push(account);
          }
        });
    }

    return accountDetailsList;
  }, []);

  const accountsDisplay = useMemo(() => {
    return accountDetails && accountDetails.length
      ? getOutageAccountDetails()
      : null;
  }, [accountDetails, getOutageAccountDetails]);

  useEffect(() => {
    const markersList: AccountMarkerInfo[] = [];
    const boundPoints: OutageLngLat[] = [];

    accountsDisplay?.slice(0, 5)?.forEach(account => {
      const {
        isPrimaryAccount,
        premiseInfos,
        accountNumber,
        encryptedPersonId,
      } = account;

      premiseInfos?.forEach(servicePoint => {
        if (!servicePoint?.servicePointDetails?.length) {
          return;
        }

        const outageServicePoint = servicePoint.servicePointDetails.find(
          outage => outage?.outageInfo?.hasKnownOutage,
        );
        const point = outageServicePoint ?? servicePoint.servicePointDetails[0];
        if (!point) {
          return;
        }

        markersList.push({
          isPrimaryAccount,
          lat: point.lat,
          lng: point.long,
          hasKnownOutage: point?.outageInfo?.hasKnownOutage || false,
          premiseInfo: servicePoint,
          accountNumber: accountNumber || '',
          encryptedPersonId: encryptedPersonId || '',
          AccountWithOutageInfo: account,
          cause: point.outageInfo?.cause || '',
          eventStatus: point.outageInfo?.eventStatus || '',
        });

        boundPoints.push({
          lat: parseFloat(point.lat),
          lng: parseFloat(point.long),
        });
      });
    });

    // Add selected account marker if it's not already in the list
    if (
      selectedAccount &&
      !markersList.some(m => m.accountNumber === selectedAccount.accountNumber)
    ) {
      const servicePoint = selectedAccount.premiseInfos?.[0];
      const point = servicePoint?.servicePointDetails?.[0];
      if (point) {
        markersList.push({
          isPrimaryAccount: selectedAccount.isPrimaryAccount,
          lat: point.lat,
          lng: point.long,
          hasKnownOutage: point?.outageInfo?.hasKnownOutage || false,
          premiseInfo: servicePoint,
          accountNumber: selectedAccount.accountNumber || '',
          encryptedPersonId: selectedAccount.encryptedPersonId || '',
          AccountWithOutageInfo: selectedAccount,
          cause: point.outageInfo?.cause || '',
          eventStatus: point.outageInfo?.eventStatus || '',
        });

        boundPoints.push({
          lat: parseFloat(point.lat),
          lng: parseFloat(point.long),
        });
      }
    }

    fitBounds(boundPoints);
    setAccountMarkersList(markersList);
  }, [accountsDisplay, selectedAccount, fitBounds]);

  return (
    <>
      {accountMarkersList.map(account => {
        const isSelected =
          selectedAccount &&
          account.accountNumber === selectedAccount.accountNumber;
        const cause = account.cause?.toLowerCase();
        const isPlanned =
          cause === 'maintenance' || account.eventStatus.startsWith('P-');
        return (
          <Marker
            key={`home-${account.accountNumber}`}
            longitude={account.lng}
            latitude={account.lat}
            anchor="center"
            style={{ zIndex: isSelected ? 999 : 3, cursor: 'pointer' }}
            onClick={() => {
              setOutageMarkerView(undefined);
              //For mobile responsive, invoke the details panel
              setPage(ResponsivePageView.Details);

              setSelectedAccountForOutage({ ...account.AccountWithOutageInfo });
            }}
          >
            <div
              onClick={e => {
                e.preventDefault(); //Prevents triggering the PSPS polygon onclick handler
              }}
            >
              {isPlanned ? (
                <PlannedOutageIcon
                  className={isSelected ? classes.iconSelected : classes.icon}
                />
              ) : (
                <>
                  {account.AccountWithOutageInfo?.accountType ===
                  AccountType.Com ? (
                    <CommercialIcon
                      className={
                        isSelected ? classes.iconSelected : classes.icon
                      }
                      color={
                        account.hasKnownOutage ? mainColors.roseRed : undefined
                      }
                    />
                  ) : (
                    <ServiceLocationIcon
                      className={
                        isSelected ? classes.iconSelected : classes.icon
                      }
                      color={
                        account.hasKnownOutage ? mainColors.roseRed : undefined
                      }
                    />
                  )}
                </>
              )}
            </div>
          </Marker>
        );
      })}
    </>
  );
};
